import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { MTable, RepealableName } from "../../../../../../Components";
import { useIntl } from "react-intl";
import { Button, LinearProgress, Theme, Tooltip, createStyles, withStyles } from "@material-ui/core";
import { useAptorApi } from "../../../../../../Api";
import { AccessLevel, LawPortalFeature, UserContext, planType } from "../../../../../../Context/UserContext/UserContext";
import { Edit } from "@material-ui/icons";
import { Query } from "@material-table/core";
import { IGridQueryRequest, OrderDirection } from "../../../../../../Api/AptorApi";
import { useSnackbar } from 'notistack';

interface BorderLinearProgressProps {
  name?: string;
  variant: 'determinate' | 'indeterminate' | 'buffer' | 'query';
  value: number;
}

interface IProps {
  planData: any
  filters: any
  activeTab?: string
  onStartCompliance: (lawData: any, planData: any) => void
  onEditCompliance: (lawData: any, planData: any) => void
  search:any
}

const BorderLinearProgress = withStyles((theme: Theme) =>
    createStyles({
      root: {
        height: 25,
        borderRadius: 12,
        boderWidth: '3px',
        borderColor: (props: BorderLinearProgressProps) =>
          props.value === 100 ? 'green' : props.value === 0 ? "red" : 'yellow',
        borderStyle: 'solid',
      },
      colorPrimary: {
        backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
      },
      bar: {
        borderRadius: 5,
        backgroundColor: (props: BorderLinearProgressProps) =>
          props.value === 100 ? 'green' : props.value === 0 ? "" : 'yellow',
      },
    }),
  )(({ ...props }: BorderLinearProgressProps) => <LinearProgress {...props} />);
const PlanningViewLK = ({ planData, filters, activeTab, onStartCompliance, onEditCompliance,search }: IProps) => {
  const tableRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const { api } = useAptorApi();
  const { firstName, lastName, hasAccessToFeature } = useContext(UserContext);
  
  const currentuserName = useMemo(() => `${firstName} ${lastName}`, [firstName, lastName]);

  const handleLawDataFetch = useCallback(
    async (query: Query<any>) => {
      return await api.searchLawPortalPlanLaws<any>(planData.id, {
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'number',
        page: query.page,
        pageSize: query.pageSize,
        search: search,
        filters: filters,
      } as IGridQueryRequest).then(res => {
        if (hasAccessToFeature(LawPortalFeature.Statistics, AccessLevel.Read)) {
          return res
        } else {
          let data = res.data.filter((val: any) => {
            const responsible = val?.responsible?.split(',')
            responsible?.forEach((item: any, index: any, array: any) => {
              array[index] = item.toLowerCase();
            });
            if (responsible?.includes(currentuserName.toLowerCase())) {
              return val
            }
          })
          res.data = data
          return res
        }
      });
    },
    [api, filters, planData.id, hasAccessToFeature, currentuserName],
  );

  const handleReqDataFetch = useCallback(
    async (query: Query<any>) => {
      return await api.searchLawPortalPlanRequirementLaws<any>(planData.id, {
        search: search,
        filters: filters,
      } as IGridQueryRequest).then(res => {
        if (hasAccessToFeature(LawPortalFeature.Statistics, AccessLevel.Read)) {
          return res
        } else {
          let data = res.data.filter((val: any) => {
            const responsible = val?.responsible?.split(',')
            responsible?.forEach((item: any, index: any, array: any) => {
              array[index] = item.toLowerCase();
            });
            if (responsible?.includes(currentuserName.toLowerCase())) {
              return val
            }
          })
          res.data = data
          return res
        }
      });
    },
    [api, filters, planData.id, hasAccessToFeature, currentuserName],
  );



  const handleComplianceSession = useCallback((event: any, lawData: any) => {
    event.stopPropagation();
    onStartCompliance(lawData, planData);
  }, [onStartCompliance, planData]);

  const handleEditSession = useCallback((lawData: any) => {
    onEditCompliance(lawData, planData);
  }, [onEditCompliance, planData]);

  const handlemsg = useCallback(() => {
    enqueueSnackbar(formatMessage({ id: 'component.law-error-description' }, { companyunitname: planData?.customerCompanyUnitId?.name }), { variant: 'error' })
  }, [enqueueSnackbar, formatMessage, planData?.customerCompanyUnitId?.name]);

  const columns = useMemo(() => [
    { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
    {
      title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
      field: 'name',
      render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
    },
    {
      title: formatMessage({ id: 'law-portal.complince.direct.table.header.status' }),
      field: 'status',
      render: (row) => <BorderLinearProgress variant="determinate" value={row?.status || 0} />,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance.tab.carryout.table.user' }),
      field: 'responsible',
      render: (row: any) => {
        if (row.responsible) {
          const responsiblearr = row?.responsible?.split(',')
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {responsiblearr?.map((val: any, idx: number) => {
                return <span key={idx}>{val}</span>
              })}
            </div>
          )
        } else {
          return "-"
        }
      },
    },
    {
      title: '',
      field: '',
      render: (row) => {
        const responsible = row?.responsible?.split(',')
        responsible?.forEach((item: any, index: any, array: any) => {
          array[index] = item.toLowerCase();
        });
        return (
          <>
            {activeTab != 'complete' && responsible?.includes(currentuserName.toLowerCase()) ? row.isStarted ?
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '7px' }}>
                <Tooltip title={formatMessage({ id: "law-portal.our-law.compliance.edit" })}>
                  <Edit style={{ cursor: 'pointer' }} onClick={() => row?.hasAccessToExecute == 0 ? handlemsg() : handleEditSession(row)} />
                </Tooltip>
              </div>
              : <Button
                variant="contained"
                onClick={(event: any) => row?.hasAccessToExecute == 0 ? handlemsg() : handleComplianceSession(event, row)}
              >
                {planData?.type === 1 ? formatMessage({ id: "law-portal.our-laws.compliance.tab.carryout.kek.create.button" }) : formatMessage({ id: "law-portal.our-laws.compliance.tab.carryout.create.button" })}
              </Button> : ""}
          </>
        );
      },
    },
  ], [formatMessage, activeTab, currentuserName, planData, handlemsg, handleEditSession, handleComplianceSession]);

  const filteredColumns = useMemo(() => {
    if (planData.type === planType.requirement) {
      return columns.filter((val) => val.field !== "number")
    }
    return columns
  }, [columns, planData.type]);

  return (
    <div style={{ paddingTop: '10px', paddingLeft: '30px', paddingRight: '30px' }}>
      <MTable
        tableRef={tableRef}
        columns={filteredColumns}
        hideToolbar
        data={planData.type === planType.requirement ? handleReqDataFetch : handleLawDataFetch}
        actionLabel={''}
      />
    </div>
  )
}

export default PlanningViewLK